import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import api from 'api/api';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import translations from 'services/translations/translations';

import 'style/offers/PackageSizes.css';

/** The amounts in a package of a product, from the product registry. */
export interface IPackageSize {
  amountInPackage: number | null;
  areaOfPackage: number | null;
  jmInPackage: number | null;
  productId: number;
}

// Read once for each product while the app is open
const cache = new Map<number, IPackageSize | null>();

/** The package sizes of the products of the window, by product id. */
export const PackageSizesContext = createContext<
  Map<number, IPackageSize | null>
>(new Map());

export const usePackageSizes = (
  productIds: number[]
): Map<number, IPackageSize | null> => {
  const [sizes, setSizes] = useState(new Map(cache));
  const missing = productIds
    .filter((id, index, all) => id > 0 && all.indexOf(id) === index)
    .filter((id) => !cache.has(id))
    .sort((a, b) => a - b);
  const missingKey = missing.join(',');

  useEffect(() => {
    if (missingKey === '') {
      return undefined;
    }

    let active = true;
    api
      .get('/api/v1/products/package-sizes', { ids: missingKey })
      .then((found: IPackageSize[]) => {
        missing.forEach((id) => cache.set(id, null));
        found.forEach((size) => cache.set(size.productId, size));
        if (active) {
          setSizes(new Map(cache));
        }
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [missingKey]);

  return sizes;
};

// The amount in a package in the unit of the quantity of the row
const getAmountInPackage = (
  size: IPackageSize | null | undefined,
  unit: string
): number | null => {
  if (size === null || size === undefined) {
    return null;
  }

  switch (unit) {
    case 'amount':
      return size.amountInPackage;
    case 'm2':
      return size.areaOfPackage;
    case 'jm':
    case 'm':
      return size.jmInPackage;
    default:
      return null;
  }
};

/**
 * The number of packages of the material of a row for the whole position, waste included,
 * rounded up, and buttons that add or take away a whole package by changing the quantity
 * of the row. Shown only for products with a package size in the registry.
 */
export const PackageStepper = ({
  disabled,
  productId,
  setTotal,
  total,
  unit,
}: {
  disabled: boolean;
  productId: number | null;
  // Sets the quantity of the row so that the position needs this much of the material
  setTotal: (total: number) => void;
  // How much of the material the position needs now, waste included
  total: number;
  unit: string;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.offers]);
  const sizes = useContext(PackageSizesContext);
  const perPackage =
    productId === null ? null : getAmountInPackage(sizes.get(productId), unit);

  if (perPackage === null || perPackage <= 0 || !Number.isFinite(total)) {
    return null;
  }

  // Rounded to a thousandth first: the quantity of the row is saved with six decimals, so
  // e.g. 11 whole packages can read back as 11,0000014
  const packages = Math.ceil(Math.round((total / perPackage) * 1000) / 1000);
  const text = (key: string) =>
    t(`${translations.calculationWindow}:packages.${key}`);

  return (
    <div className="package-stepper" title={text('Info')}>
      <button
        aria-label={text('Remove')}
        disabled={disabled || packages <= 0}
        onClick={() => setTotal(Math.max(packages - 1, 0) * perPackage)}
        type="button"
      >
        −
      </button>
      <span className="package-stepper-count">
        {`${packages} ${text('Short')}`}
        <span className="package-stepper-size">
          {` à ${formatGroupedNumber(perPackage, 2, 0)}`}
        </span>
      </span>
      <button
        aria-label={text('Add')}
        disabled={disabled}
        onClick={() => setTotal((packages + 1) * perPackage)}
        type="button"
      >
        +
      </button>
    </div>
  );
};
